// 이 쪽의 그림: the pictures an output page shows that are chosen somewhere they can change — a product's cover
// scene and topics, the icon each meaning is shown by — each swapped there for a picture of its kind shaped like it. The
// page's parts name where each picture is chosen (part.edits, the builder's book.picture addresses); the
// output's layout editor says what each place is, keeps the swaps with the layout edits and serves the pictures.

function node(tag, attributes = {}, children = []) {
  const element = document.createElement(tag);
  for (const [name, value] of Object.entries(attributes)) {
    if (name === 'text') element.textContent = value;
    else element.setAttribute(name, value);
  }
  element.append(...children);
  return element;
}
const ICON = 'picture/icon/';
const isTopic = address => address.includes('/topic/');
/** The kind of picture a place takes: a meaning's icon an icon, a product's scene a cover scene. */
const kindOf = address => (address.startsWith(ICON) ? 'icon' : 'scene');

/**
 * The page's swappable pictures, one per place a picture is chosen — the product's own first, then the books'
 * icons: { address, asset (the file drawn), meaning (for a product's topic: the meaning its icon shows), boxes }.
 */
export function picturesOf(parts) {
  const found = new Map();
  for (const part of parts) {
    if (part.kind !== 'image' || !part.edits?.length) continue;
    const icon = part.edits.find(address => address.startsWith(ICON));
    for (const address of part.edits) {
      const entry = found.get(address) ?? { address, asset: part.asset, meaning: icon ? icon.slice(ICON.length) : null, boxes: [] };
      entry.boxes.push([part.x, part.y, part.w, part.h]);
      found.set(address, entry);
    }
  }
  const shared = address => address.startsWith(ICON);
  return [...found.values()].sort((a, b) => shared(a.address) - shared(b.address));
}

/** The library's pictures shaped like `file` (within 4%, as the workshop checks): a picture is drawn in the box of the one it replaces. */
export function shapedLike(library, file) {
  const current = library.find(entry => entry.file === file);
  if (!current) return [];
  const ratio = current.w / current.h;
  return library.filter(entry => Math.abs(entry.w / entry.h / ratio - 1) <= 0.04);
}

/**
 * The pictures a place (address, drawn with `asset`) may take — of its kind (library.kinds, as the workshop checks:
 * an icon for an icon, a cover scene for a cover scene) and shaped like its own.
 */
export function swapsFor(library, address, asset) {
  const kind = new Set(library.kinds[kindOf(address)]);
  return shapedLike(library.pictures, asset).filter(entry => kind.has(entry.file));
}

/**
 * editor: the output's layout editor — pictures() ({ pictures: [{ file, w, h }], icons: { meaning: file }, kinds: { icon, scene: [file] } }),
 * pictureUrl(file), pictureLabel(address) ({ label, scope }), meaningName(meaning), save, revert. onEdited(pending)
 * hears a swap or an undo; onLight(boxes, on) lights a picture's boxes on the page while its row is pointed at.
 */
export function mountPagePictures({ onEdited = () => {}, onLight = () => {} } = {}) {
  const heading = node('h4', { class: 'es-page-pictures-heading', text: '이 쪽의 그림' });
  const note = node('p', { class: 'es-page-pictures-note', 'data-testid': 'studio-page-pictures-note' });
  const list = node('ol', { class: 'es-page-pictures-list', 'data-testid': 'studio-page-pictures' });
  const picker = node('div', { class: 'es-page-pictures-picker', hidden: '', 'data-testid': 'studio-page-picture-picker' });
  const status = node('p', { class: 'es-page-pictures-status', 'aria-live': 'polite', 'data-testid': 'studio-page-pictures-status' });
  const element = node('section', { class: 'es-page-pictures', hidden: '', 'data-testid': 'studio-page-pictures-section' }, [heading, note, list, picker, status]);
  let pictures = [], editor = null, pending = [], library = null, ticket = 0;

  const pendingOf = picture => pending.find(edit => edit.address === picture.address);
  /** What the place holds now, its pending swap included: a file, or for a product's topic its meaning. */
  function valueOf(picture) {
    const edit = pendingOf(picture);
    if (edit) return edit.value;
    return isTopic(picture.address) ? picture.meaning : picture.asset;
  }
  const fileOf = picture => (isTopic(picture.address) ? library.icons[valueOf(picture)] : valueOf(picture));

  function draw() {
    list.replaceChildren(...pictures.map(picture => {
      const { label, scope } = editor.pictureLabel(picture.address);
      const edited = Boolean(pendingOf(picture));
      const change = node('button', { type: 'button', class: 'es-page-pictures-change', 'data-testid': 'studio-page-picture-change', text: '바꾸기' });
      change.addEventListener('click', () => open(picture));
      const undo = node('button', { type: 'button', class: 'es-page-pictures-undo', 'data-testid': 'studio-page-picture-revert', text: '원래대로', ...(edited ? {} : { hidden: '' }) });
      undo.addEventListener('click', () => { void revert(picture); });
      const row = node('li', { class: `es-page-pictures-item${edited ? ' es-page-pictures-item--edited' : ''}`, 'data-address': picture.address, 'data-testid': 'studio-page-picture' }, [
        node('img', { src: editor.pictureUrl(fileOf(picture)), alt: '', width: '44', height: '44' }),
        node('span', { class: 'es-page-pictures-label' }, [node('strong', { text: label }), node('small', { text: scope })]), change, undo]);
      row.addEventListener('mouseenter', () => onLight(picture.boxes, true));
      row.addEventListener('mouseleave', () => onLight(picture.boxes, false));
      return row;
    }));
  }
  function open(picture) {
    const current = valueOf(picture);
    // A product's topic is one of the meanings (its icon follows); any other place, a picture of its kind shaped like its own.
    const options = isTopic(picture.address)
      ? Object.entries(library.icons).map(([meaning, file]) => ({ value: meaning, file, name: editor.meaningName(meaning) }))
      : swapsFor(library, picture.address, picture.asset).map(entry => ({ value: entry.file, file: entry.file, name: '' }));
    const close = node('button', { type: 'button', class: 'es-page-pictures-close', 'data-testid': 'studio-page-picture-picker-close', text: '닫기' });
    close.addEventListener('click', () => { picker.hidden = true; });
    picker.replaceChildren(
      node('p', { class: 'es-page-pictures-picker-title', text: `${editor.pictureLabel(picture.address).label} · ${isTopic(picture.address) ? '뜻' : '그림'}을 고르면 바로 저장합니다` }),
      node('div', { class: 'es-page-pictures-grid' }, options.map(option => {
        const button = node('button', { type: 'button', class: `es-page-pictures-option${option.value === current ? ' es-page-pictures-option--current' : ''}`,
          'data-value': option.value, 'data-testid': 'studio-page-picture-option', title: option.name || option.value, 'aria-pressed': String(option.value === current) },
        [node('img', { src: editor.pictureUrl(option.file), alt: option.name || option.value, loading: 'lazy' }), ...(option.name ? [node('small', { text: option.name })] : [])]);
        button.addEventListener('click', () => { void choose(picture, option.value); });
        return button;
      })), close);
    picker.dataset.address = picture.address;
    picker.hidden = false;
  }
  async function choose(picture, value) {
    status.textContent = '저장하는 중…';
    try {
      pending = await editor.save({ address: picture.address, base: valueOf(picture), value });
      picker.hidden = true;
      status.textContent = `저장했습니다 · ${editor.pictureLabel(picture.address).label} — 미리보기로 실제 모습을 봅니다`;
      onEdited(pending);
    } catch (error) { status.textContent = error.message; }
    draw();
  }
  async function revert(picture) {
    try {
      pending = await editor.revert(picture.address);
      status.textContent = `${editor.pictureLabel(picture.address).label}을(를) 원래대로 되돌렸습니다`;
      onEdited(pending);
    } catch (error) { status.textContent = error.message; }
    draw();
  }
  /** Lists the pictures a page's parts show; the adapter's `note` explains where its picture changes apply. */
  async function show(parts, { editor: pictureEditor = null, pending: known = [], note: description = '그림을 바꾸면 각 항목에 표시된 범위에 반영됩니다.' } = {}) {
    const mine = ++ticket;
    editor = pictureEditor; pending = known; picker.hidden = true; status.textContent = '';
    note.textContent = description; note.hidden = !description;
    pictures = editor ? picturesOf(parts) : [];
    element.hidden = !pictures.length;
    list.replaceChildren();
    if (!pictures.length) return;
    try { library = await editor.pictures(); } catch (error) { status.textContent = error.message; return; }
    if (mine !== ticket) return;
    draw();
  }
  /** The pending design edits, changed beside the list (a layout value saved or undone). */
  function update(known) { pending = known; if (!element.hidden && library) draw(); }
  function clear() { ticket += 1; pictures = []; element.hidden = true; list.replaceChildren(); picker.hidden = true; }
  return { element, show, update, clear };
}
